import {Component, Inject, LOCALE_ID} from '@angular/core';
import {Role} from '../../../model/auth/Role';
import {MobileAwareComponent} from '../../../common/mobile-aware/mobile-aware.component';
import {BreakpointObserver} from '@angular/cdk/layout';
import {Contest} from '../../../model/contest/Contest';
import {LocationExamStats, StatsGroupingType} from '../../../model/stats/Stats';
import {NotificationHandler} from '../../../helper/notification.handler';
import {StatsService} from '../../../service/stats.service';
import {DEFAULT_PAGE, Page} from '../../../model/Page';
import {School} from '../../../model/location/School';
import {LocationService} from '../../../service/location.service';
import {TenantService} from '../../../service/tenant.service';
import {ContestTaskType, localizeContestTaskType} from '../../../model/contest/ContestTask';
import {Locale} from '../../../model/Locale';
import {ActivatedRoute} from '@angular/router';
import {ContestService} from '../../../service/contest.service';
import {MatSelectionListChange} from '@angular/material/list';

interface SchoolData {
  school: School;
  stats: LocationExamStats;
  selected: boolean;
}

@Component({
  selector: 'app-exam-region-schools-result-stats',
  templateUrl: './exam-region-schools-result-stats.component.html',
  styleUrls: ['./exam-region-schools-result-stats.component.scss']
})
export class ExamRegionSchoolsResultStatsComponent extends MobileAwareComponent {

  private readonly PAGE_SIZE = 200;

  readonly roles = [Role.Host, Role.Coordinator];

  contestId?: string;
  regionId?: string;

  competitionId?: string;

  contest?: Contest;

  regions: Map<string, string> = new Map<string, string>();

  schools = new Map<string, SchoolData[]>();
  schoolRegions: string[] = [];
  schoolStats: LocationExamStats[] = [];

  inProgress = false;

  allSelected = true;

  page: Page<never> = DEFAULT_PAGE;

  constructor(private statsService: StatsService,
              private contestService: ContestService,
              private locationService: LocationService,
              private tenantService: TenantService,
              private route: ActivatedRoute,
              @Inject(LOCALE_ID) private locale: string,
              breakpointObserver: BreakpointObserver) {
    super(breakpointObserver);
  }

  initContent() {
    this.inProgress = true;

    const tenant = this.tenantService.getTenant();
    if (tenant && tenant.id) this.competitionId = tenant.id;

    this.route.params.subscribe(params => {
      if (params) {
        if (params.contestId) this.contestId = params.contestId as string;
        if (params.regionId && params.regionId != '-1') this.regionId = params.regionId as string;

        this.loadContest();
        this.loadRegionExamStats(true);
      }
    });

    this.loadRegions();
  }

  private loadRegions(): void {
    this.locationService.getRegions(this.locale as Locale).subscribe(regions => {
      regions.forEach(region => this.regions.set(region.id, region.name));
    });
  }

  private loadContest() {
    if (!this.contestId) return;

    this.contestService.getDetails(this.contestId).subscribe(contest => {
      this.contest = contest;
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|Contest details fetch has failed message@@notifications-contest-details-fetch-failed-error:Contest details fetch has failed`, error);
      this.inProgress = false;
    });
  }

  loadRegionExamStats(reset = false) {
    if (!this.competitionId || !this.contestId || !this.regionId) return;

    if (reset) {
      this.page = {
        max_items: this.PAGE_SIZE,
        asc: false
      };
      this.schools.clear();
      this.schoolRegions = [];
      this.schoolStats = [];
    }

    this.inProgress = true;

    this.statsService.getRegionExamStats(this.contestId, this.regionId, StatsGroupingType.school, this.page).subscribe(page => {
      const data = page.content || [];
      if (reset) {
        this.schoolStats = data;
      } else {
        if (!this.schoolStats) {
          this.schoolStats = data;
        } else {
          this.schoolStats = this.schoolStats.concat(data);
        }
      }

      const schools = new Map<string, SchoolData[]>();
      const regions = new Set<string>();
      this.schoolStats.forEach(stats => {
        const school = new School(stats.schoolId as string, stats.schoolName || '');
        school.address = stats.schoolAddress;

        let region = stats.schoolRegionName || '';
        const regionParts = region.split(',');
        if (regionParts.length == 3) {
          const city = regionParts[0];
          regionParts[0] = regionParts[1].trim();
          regionParts[1] = city.trim();
          regionParts[2] = regionParts[2].trim();

          region = regionParts.join(', ');
          school.region_name = region;
        }
        if (!schools.has(region)) schools.set(region, []);

        (schools.get(region) as SchoolData[]).push({
          school: school,
          stats: stats,
          selected: true
        });
        regions.add(region);
      });

      schools.forEach((s, region) => {
        s.sort((s1, s2) => s1.school.name.localeCompare(s2.school.name));
      });
      this.schoolRegions = Array.from(regions);
      this.schoolRegions.sort();
      this.schools = schools;

      this.page.next_token = page.next_token;

      if (this.page.next_token) {
        this.loadRegionExamStats();
      } else {
        this.inProgress = false;
      }
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|Stats fetch has failed message@@notifications-stats-fetch-failed-error:Statistical data fetch has failed`, error);
    });
  }

  localizeTaskType(taskType?: ContestTaskType) {
    return taskType ? localizeContestTaskType(taskType) : '';
  }

  get filteredSchoolStats(): LocationExamStats[] {
    const stats: LocationExamStats[] = [];

    this.schools.forEach((schools, region) => {
      schools
        .filter(school => school.selected)
        .forEach(school => stats.push(school.stats));
    });

    return stats;
  }

  selectAllSchools(checked: boolean) {
    this.allSelected = checked;

    if (this.allSelected) {
      this.schools.forEach((schools, region) => {
        schools.forEach(school => school.selected = true);
      });
    } else {
      this.schools.forEach((schools, region) => {
        schools.forEach(school => school.selected = false);
      });
    }
  }

  handleSchoolSelection(change: MatSelectionListChange) {
    change.options.forEach(option => (option.value as SchoolData).selected = option.selected);

    this.allSelected = Array.from(this.schools.values())
      .filter(schools => schools.findIndex(s => !s.selected) >= 0)
      .length == 0;
  }

  isRegionSelected(region: string) {
    return Array.from(this.schools.get(region) || [])
      .filter(school => !school.selected)
      .length == 0;
  }

  selectRegionSchools(checked: boolean, region: string) {
    (this.schools.get(region) || []).forEach(school => school.selected = checked);

    this.allSelected = Array.from(this.schools.values())
      .filter(schools => schools.findIndex(s => !s.selected) >= 0)
      .length == 0;
  }
}
